import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';

import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { ChartDataset, CustomerKPITotals } from '../interfaces/kpis/customer-kpi-totals';
import { BaseDataService } from './base.data.service';
import { KPILivefeed } from '../interfaces/kpis/kpi-livefeed';
import FileSaver from 'file-saver';
import { Message } from '../classes/Message';
import { MessageService } from 'primeng';

export enum ValueType {
  Percent,
  Number,
  Currency,
  Date,
  String,
}

export interface IDataLabel {
  display: boolean;
  align?: string;
  anchor?: string;
  borderRadius?: number;
  backgroundColor?: string;
  color?: string;
  font: {
    weight?: string;
  };
  formatter: any;
}

@Injectable({ providedIn: 'root' })
export class DashboardCustomerKPIService extends BaseDataService {
  url = '/api/kpi';
  entityTitle = 'KPI';

  public isLoading = false;

  public stackedChartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 15,
            userCallback: function (label, index, labels) {
              // when the floored value is the same as the value we have a whole number
              if (Math.floor(label) === label) {
                return label;
              }
            },
          },
          stacked: true,
          display: false,
        },
      ],
      xAxes: [
        {
          stacked: true,
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    gridLines: { display: false },
    borderWidth: 2,
    plugins: {
      datalabels: {
        font: {
          weight: 'bold',
        },
        formatter: (value, ctx) => this.percentFormatter(value, ctx),
      },
    },
  };

  baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            padding: 15,
          },
          display: false,
        },
      ],
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            offset: true,
            padding: 10,
          },
        },
      ],
    },
    gridLines: { display: false },
    borderWidth: 2,
    plugins: {
      datalabels: {
        align: 'center',
        anchor: 'center',
        borderRadius: 4,
        backgroundColor: 'transparent',
        color: 'black',
        font: {
          weight: 'bold',
        },
      },
    },
  };

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  private dateFrom: Date;
  private rangeType: number;
  private rangeValue: number;
  private deptId: number;
  private orderTagId: number;

  public completedTotals: CustomerKPITotals[];
  public liveFeedData: KPILivefeed[];

  public gradeColourStart = 'rgba(255, 118, 13, 1)'; // 'rgba(252, 149, 76, 1)';
  public gradeColourEnd = 'rgba(76, 252, 135, 1)';

  // manually created grading colours look better (skips the brown)
  public gradeColours = [
    'rgba(255, 138, 48,1)',
    'rgba(255, 193, 48,1)',
    'rgba(235, 215, 5,1)',
    'rgba(181, 224, 0,1)',
    'rgba(150, 220, 64,1)',
    'rgba(0,190,20,1)',
  ];

  public async loadCustomerKPIData(
    dateFrom: Date,
    dateRangeType: number,
    dateRangeValue: number,
    deptId: number,
    orderTagId: number,
  ) {
    // no need to load data if params are the same as previous
    if (
      dateFrom == this.dateFrom &&
      dateRangeType == this.rangeType &&
      dateRangeValue == this.rangeValue &&
      deptId == this.deptId &&
      orderTagId == this.orderTagId
    ) {
      return;
    }

    this.isLoading = true;

    this.dateFrom = dateFrom;
    this.rangeType = dateRangeType;
    this.rangeValue = dateRangeValue;
    this.deptId = deptId;
    this.orderTagId = orderTagId;

    this.completedTotals = await this.getCustomerKPITotals();
    this.liveFeedData = await this.getCustomerLiveFeedData();

    //console.log("KPI SERVICE Live Feed: ", this.liveFeedData);
    console.log('KPI SERVICE Completed Totals: ', this.completedTotals);
    this.isLoading = false;
  }

  private getCustomerKPITotals(): Promise<CustomerKPITotals[]> {
    return this.handleRequest(
      this.http.get<CustomerKPITotals[]>(
        `${this.url}/getCustomerKPITotals/${this.rangeValue}/${this.rangeType}/${this.deptId}/${
          this.orderTagId
        }?fromDate=${this.dateFrom.toISOString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  private getCustomerLiveFeedData(): Promise<KPILivefeed[]> {
    return this.handleRequest(
      this.http.get<KPILivefeed[]>(
        `${this.url}/getCustomerLiveFeedData/${this.rangeValue}/${this.rangeType}/${this.deptId}/${
          this.orderTagId
        }?fromDate=${this.dateFrom.toISOString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public async downloadToCSV() {
    // get the start and end date using the API
    let dateRange = await this.getDateRange();
    console.log('DATE RANGE: ', dateRange);

    //_${moment().format("YYYYMMMMDo_h:mm")}
    let startDate = new Date(dateRange.from); // this.getStartDate();
    let endDate = new Date(dateRange.to); // this.getEndDate(startDate);

    let filename = `KPI_Data_${moment(startDate).format('YYYY-MM-DD')}_to_${moment(endDate).format(
      'YYYY-MM-DD',
    )}.csv`;

    let url = `${
      this.url
    }/CustomerDownloadToCSV/${startDate.toISOString()}/${endDate.toISOString()}/${this.deptId}/${
      this.orderTagId
    }`;
    let report = this.handleRequest(
      this.http.get(url, { responseType: 'blob' }),
      this.handlersFactory.getForReadAll(),
    ).then((result) => {
      this.handleDownloadFile(result, filename);
      this.messageService.add(
        Message.Success('Downloaded successful. Check your download folder.'),
      );
    });

    return report;
  }

  public getDateRange() {
    if (this.rangeType == 2 && this.rangeValue == -1) {
      return { from: moment().subtract(12, 'weeks').toDate(), to: moment().toDate() };
    }

    let from = moment(this.dateFrom).startOf('month').toDate();
    let to = from;

    switch (this.rangeType) {
      case 1: // daily
        to = moment(to).add(this.rangeValue, 'days').toDate();
        break;
      case 2: // weekly
        to = moment(to)
          .add(this.rangeValue * 7, 'days')
          .toDate();
        break;
      case 3: // monthly
        to = moment(to).add(this.rangeValue, 'months').toDate();
        break;
    }

    return { from, to };
  }

  // private getDateRange(): Promise<any> {
  //
  //   return this.handleRequest(
  //       this.http.get<KPILivefeed[]>(
  //           `${this.url}/getCustomerKPIDateRange/${this.rangeValue}/${this.rangeType}?fromDate=${this.dateFrom.toISOString()}`,
  //       ),
  //       this.handlersFactory.getForReadAll(),
  //   );
  //
  // }

  public handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  private getStartDate(): Date {
    let monthStart = moment(this.dateFrom).startOf('month');
    let startDate = monthStart;

    return startDate.toDate();
  }

  private getEndDate(startDate: Date): Date {
    switch (this.rangeType) {
      case 1: // daily
        return moment(startDate)
          .add(this.rangeValue - 1, 'days')
          .toDate();
      case 2: // weekly
        return moment(startDate).add(this.rangeValue, 'weeks').toDate();
      case 3: // monthly
        return moment(startDate).add(this.rangeValue, 'months').toDate();
    }

    return startDate;
  }

  public percentFormatter(value, ctx) {
    if (ctx.datasetIndex > 0) {
      let value1 = ctx.chart.data.datasets[0].data[ctx.dataIndex];
      if (value1 == 0) {
        return '0.0%';
      }
      let pv = (value / value1) * 100;
      let percentage = pv.toFixed(1) + '%';
      return percentage;
    } else {
      return value;
    }
  }

  public getTotalsRow(datas: ChartDataset[]) {
    let tr = [];

    if (!datas || !datas[0] || !datas[0].data || datas[0].data.length == 0) return tr;

    tr.push('Totals');

    let hasPercentCol = datas.filter((x) => x.isPercentCol).length > 0;

    // any column which is not completed moves needs to calculate the overall values as percentage of total
    let completedMoves = datas[0].data.reduce((a, b) => a + b || 0);

    datas.forEach((data, index) => {
      if (data.isPercentCol) {
        // use preceding column as value base for percentage
        let vp = datas[index - 1].data.reduce((a, b) => a + b || 0);
        let perc = completedMoves == 0 ? 0 : ((vp / completedMoves) * 100.0).toFixed(2) + '%';

        tr.push(perc);
      } else {
        let v = data.data.reduce((a, b) => a + b || 0);

        if (index > 0 && !hasPercentCol) {
          let p = v > 0 ? ((v / tr[1]) * 100.0).toFixed(2) + '%' : 0;
          tr.push(p);
        } else {
          tr.push(v);
        }
      }
    });

    return tr;
  }

  public getMetricTableData(datas: ChartDataset[], isLiveFeed = false) {
    // indexes of data [0]=Date, [1]=Completed count, [2...] values from datas

    // each column value (after index 1 [completed]) can be shown as a percentage of the completion, alongside its actual value
    let hasPercentCol = datas.filter((x) => x.isPercentCol).length > 0;

    let tableData = [];

    let dateTimes: { dateTime: Date }[] = [];
    if (isLiveFeed) {
      dateTimes = this.liveFeedData.map((x) => {
        return { dateTime: x.dateTime };
      });
    } else {
      dateTimes = this.completedTotals.map((x) => {
        return { dateTime: x.date };
      });
    }

    if (datas && datas.length > 0) {
      dateTimes.forEach((x, index) => {
        let xDate = x.dateTime;

        let it = [];
        it.push({ header: 'Date', value: xDate, type: ValueType.Date });

        for (let i = 0; i < datas.length; i++) {
          let v = datas[i].data[index];
          if (!v) {
            continue;
          }

          // if we have a percentage column specified, just use raw value in column (rather than percentage of complete)
          if (hasPercentCol) {
            // if this is the percent column, add percent sign
            if (datas[i].isPercentCol)
              it.push({ header: datas[i].label, value: v.toFixed(1), type: ValueType.Percent });
            else it.push({ header: datas[i].label, value: v, type: ValueType.Number });
          } else {
            // calculate percentage of completed moves column
            let perc = `0.0%   (${v.toString()})`;
            if (datas[0].data[index] > 0) {
              perc = `${((datas[i].data[index] / datas[0].data[index]) * 100.0).toFixed(
                1,
              )}%   (${v})`;
            }

            let p = i > 0 ? perc : v;

            it.push({ header: datas[i].label, value: p, type: ValueType.String });
          }
        }

        tableData.push(it);
      });
    }

    return tableData;
  }

  public filterZeroData(datas: ChartDataset[]): ChartDataset[] {
    return datas.filter((d) => d.data.length > 0 && d.data.reduce((x, y) => x + y) > 0);
  }

  public getServiceMetricData(
    title: string,
    datas: ChartDataset[],
    labels: string[],
    completedAsLine = false,
    options: any,
  ) {
    let values = [].concat.apply(
      [],
      datas.map((x) => x.data),
    );

    let labels1 = <IDataLabel>{
      display: true,
      align: 'center',
      anchor: 'center',
      borderRadius: 4,
      backgroundColor: 'rgba(51, 51, 51, 0.5)',
      color: 'white',
      font: {
        weight: 'bold',
      },
    };

    let labels2 = <IDataLabel>{
      display: true,
      align: 'end',
      anchor: 'end',
      borderRadius: 4,
      backgroundColor: 'transparent',
      color: 'rgba(51, 51, 51, 1)',
      font: {
        weight: 'bold',
      },
    };

    let datasets = datas
      .filter((x) => !x.hideInChart)
      .map((data, index) => {
        if (completedAsLine) {
          return index == 0 && data.type == 'line'
            ? {
                type: 'line',
                ...data,
                fill: false,
                borderColor: data.backgroundColor, // 'rgba(51, 51, 51, 0.5)'
                lineTension: 0,
                order: index,
                datalabels: labels2,
              }
            : {
                type: 'bar',
                ...data,
                fill: false,
                borderColor: 'rgba(255,255,255,1)',
                order: index,
                datalabels: labels1,
              };
        } else {
          return index == 0
            ? {
                ...data,
                fill: true,
                datalabels: labels1,
                borderColor: 'rgba(255,255,255,1)',
                type: 'bar',
                order: datas.length,
              }
            : {
                ...data,
                fill: false,
                datalabels: labels2,
                borderColor: data.backgroundColor,
                type: 'line',
                lineTension: 0,
                order: index,
              };
        }
      });

    datasets = datasets.sort((a, b) => a.order - b.order);
    //console.log("Datasets: ", datasets);

    let chartData = {
      chartType: 'bar',
      labels,
      options,
      datasets,
    };

    // create the data array to be shown in table form
    let tableData = this.getMetricTableData(datas, false);

    let headers = [];
    if (tableData.length > 0) {
      headers = tableData[0].map((x) => x.header);

      //console.log("Table DATA: ", tableData, " - headers: ", headers);
    }

    return {
      chartData,
      title,
      liveFeed: true,
      valueType: ValueType.Number,
      data: values,
      headers,
      tableData,
      totalsRow: this.getTotalsRow(datas),
    };
  }

  getGradeColours6(): string[] {
    return this.gradeColours;
  }

  getGradeColours4(): string[] {
    return [this.gradeColours[0], this.gradeColours[2], this.gradeColours[4], this.gradeColours[5]];
  }

  getInverseGradeColours4(): string[] {
    return [this.gradeColours[5], this.gradeColours[4], this.gradeColours[2], this.gradeColours[0]];
  }

  getGradeColours(steps: number): string[] {
    return this.interpolateColours(this.gradeColourStart, this.gradeColourEnd, steps);
  }

  getInverseGradeColours(steps: number): string[] {
    return this.interpolateColours(this.gradeColourEnd, this.gradeColourStart, steps);
  }

  interpolateColours(startRGB: string, endRGB: string, steps: number): string[] {
    let rgbAValues = this.getRGBAValues(startRGB);
    let rgbBValues = this.getRGBAValues(endRGB);

    let fromRed = rgbAValues[0]; //the red value as integer
    let fromGreen = rgbAValues[1];
    let fromBlue = rgbAValues[2];

    let toRed = rgbBValues[0];
    let toGreen = rgbBValues[1];
    let toBlue = rgbBValues[2];

    let difRed = toRed - fromRed; //difference between the two
    let difGreen = toGreen - fromGreen;
    let difBlue = toBlue - fromBlue;

    let incrementPercentage = 1 / (steps - 1); //how much to increment percentage by
    let colours = [];

    for (let n = 0; n < steps; n++) {
      let percentage = n * incrementPercentage; //calculate percentage
      let red = (difRed * percentage + fromRed).toFixed(0); //round em for legibility
      let green = (difGreen * percentage + fromGreen).toFixed(0);
      let blue = (difBlue * percentage + fromBlue).toFixed(0);
      let colour = 'rgba(' + red + ',' + green + ',' + blue + ',1)';
      colours.push(colour);
    }

    return colours;
  }

  getRGBAValues(string) {
    let cleaned = string.substring(string.indexOf('(') + 1, string.length - 1);
    let split = cleaned.split(',');
    let intValues = [];
    for (const index in split) {
      intValues.push(parseInt(split[index]));
    }
    return intValues;
  }
}
